import './styles/GlobalStyles.css';
import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useNavigate, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { UserPreferencesProvider, useUserPreferences } from './context/UserPreferencesContext';
import { AuthProvider, useAuth, LoginPage, SignupPage, ForgotPasswordPage, UserProfile } from './auth';
import './styles/theme.css';
import {
  LayoutDashboard,
  TrendingUp,
  Brain,
  Wallet,
  Settings,
  Search,
  User,
  Bitcoin,
  Newspaper,
  ChevronDown,
  ChevronRight,
  Languages,
  Bell,
  LogOut,
  Check,
  Crown,
  Database
} from 'lucide-react';

// Import Pages
import NewsPage from './pages/NewsPage';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import MarketsPage from './pages/MarketsPage';
import CryptoPage from './pages/CryptoPage';
import AnalysisPage from './pages/AnalysisPage';
import PortfolioPage from './pages/PortfolioPage';
import SettingsPage from './pages/SettingsPage';
import StockPage from './pages/StockPage';
import PricingPage from './pages/PricingPage';

// Import DataSets Pages
import CongressPage from './pages/DataSets/CongressPage';
import PoliticianPage from './pages/DataSets/PoliticianPage';
import InsiderTradingPage from './pages/DataSets/InsiderTradingPage';
import InsiderProfilePage from './pages/DataSets/InsiderProfilePage';
import InstitutionalHoldingsPage from './pages/DataSets/InstitutionalHoldingsPage';
import InstitutionProfilePage from './pages/DataSets/InstitutionProfilePage';
import LobbyingPage from './pages/DataSets/LobbyingPage';
import LobbyingDetailPage from './pages/DataSets/LobbyingDetailPage';
import GovernmentContractsPage from './pages/DataSets/GovernmentContractsPage';
import ContractDetailPage from './pages/DataSets/ContractDetailPage';
import WhaleMovesPage from './pages/DataSets/WhaleMovesPage';
import ETFHoldingsPage from './pages/DataSets/ETFHoldingsPage';
import ThirteenFPage from './pages/DataSets/ThirteenFPage';
import PoliticalDonationsPage from './pages/DataSets/PoliticalDonationsPage';
import SenateDisclosuresPage from './pages/DataSets/SenateDisclosuresPage';

// Import Components
import FullscreenChart from './components/FullscreenChart';
import AIChatWidget from './components/AIChatWidget';

import './App.css';

// ============================================
// PROTECTED ROUTE WRAPPER
// ============================================
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-spinner" />
        <span>Loading...</span>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// ============================================
// AUTH PAGES WRAPPER
// ============================================
function AuthRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  
  if (loading) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-spinner" />
        <span>Loading...</span>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return React.cloneElement(children, {
    onNavigate: (page) => navigate(`/${page}`),
    onSuccess: () => navigate('/dashboard')
  });
}

// ============================================
// ELEMENTAL LOGO COMPONENT
// ============================================
function ElementalLogo({ collapsed }) {
  return (
    <div className="elemental-logo">
      <div className="logo-icon">
        <div className="atom-spin-wrapper">
          <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="atom-logo">
            <defs>
              <linearGradient id="logo-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#667eea" />
                <stop offset="100%" stopColor="#764ba2" />
              </linearGradient>
              <filter id="logo-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
              <path id="orbitPath1" d="M 92,50 A 42,16 0 1,1 8,50 A 42,16 0 1,1 92,50" fill="none" />
              <path id="orbitPath2" d="M 92,50 A 42,16 0 1,1 8,50 A 42,16 0 1,1 92,50" fill="none" transform="rotate(60 50 50)" />
              <path id="orbitPath3" d="M 92,50 A 42,16 0 1,1 8,50 A 42,16 0 1,1 92,50" fill="none" transform="rotate(120 50 50)" />
            </defs>
            <ellipse cx="50" cy="50" rx="42" ry="16" stroke="url(#logo-grad)" strokeWidth="2" fill="none" opacity="0.6" className="logo-orbit logo-orbit-1"/>
            <ellipse cx="50" cy="50" rx="42" ry="16" stroke="url(#logo-grad)" strokeWidth="2" fill="none" opacity="0.6" className="logo-orbit logo-orbit-2" transform="rotate(60 50 50)"/>
            <ellipse cx="50" cy="50" rx="42" ry="16" stroke="url(#logo-grad)" strokeWidth="2" fill="none" opacity="0.6" className="logo-orbit logo-orbit-3" transform="rotate(120 50 50)"/>
            <circle cx="50" cy="50" r="8" fill="url(#logo-grad)" filter="url(#logo-glow)" className="logo-nucleus" />
            <circle r="4" fill="url(#logo-grad)" filter="url(#logo-glow)" className="logo-electron">
              <animateMotion dur="4s" repeatCount="indefinite" begin="0s"><mpath href="#orbitPath1" /></animateMotion>
            </circle>
            <circle r="4" fill="url(#logo-grad)" filter="url(#logo-glow)" className="logo-electron">
              <animateMotion dur="4s" repeatCount="indefinite" begin="-1.33s"><mpath href="#orbitPath2" /></animateMotion>
            </circle>
            <circle r="4" fill="url(#logo-grad)" filter="url(#logo-glow)" className="logo-electron">
              <animateMotion dur="4s" repeatCount="indefinite" begin="-2.67s"><mpath href="#orbitPath3" /></animateMotion>
            </circle>
          </svg>
        </div>
      </div>
      {!collapsed && (
        <span className="logo-text" style={{
          fontSize: '1.15rem',
          fontWeight: 700,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          letterSpacing: '1px'
        }}>
          ELEMENTAL
        </span>
      )}
    </div>
  );
}

// ============================================
// DATASETS MEGA DROPDOWN (LEAN VERSION)
// ============================================
function DatasetsMegaDropdown({ isOpen, onClose, onNavigate }) {
  const datasets = {
    popular: {
      title: 'Popular',
      items: [
        { name: 'Congress Trading', path: '/datasets/congress' },
        { name: 'Insider Trading', path: '/datasets/insider' },
        { name: 'Whale Moves', path: '/datasets/whales' },
        { name: 'Institutional Holdings', path: '/datasets/institutional' },
      ]
    },
    signals: {
      title: 'Signals',
      items: [
        { name: 'Insider Trading', path: '/datasets/insider' },
        { name: 'Whale Moves', path: '/datasets/whales' },
        { name: 'ETF Holdings', path: '/datasets/etf-holdings' },
      ]
    },
    filings: {
      title: 'Filings',
      items: [
        { name: 'Institutional Holdings', path: '/datasets/institutional' },
        { name: '13F Filings', path: '/datasets/13f' },
        { name: 'Congress Trading', path: '/datasets/congress' },
      ]
    },
    influence: {
      title: 'Influence',
      items: [
        { name: 'Lobbying Data', path: '/datasets/lobbying' },
        { name: 'Government Contracts', path: '/datasets/gov-contracts' },
        { name: 'Political Donations', path: '/datasets/donations' },
        { name: 'Senate Disclosures', path: '/datasets/senate-disclosures' },
      ]
    },
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="mega-dropdown-overlay" onClick={onClose} />
      <div className="mega-dropdown">
        <div className="mega-dropdown-content">
          {Object.entries(datasets).map(([key, category]) => (
            <div key={key} className="mega-dropdown-column">
              <h4 className="mega-column-title">{category.title}</h4>
              <ul className="mega-column-list">
                {category.items.map((item, idx) => (
                  <li key={idx}>
                    <button 
                      className="mega-dropdown-item"
                      onClick={() => { onNavigate(item.path); onClose(); }}
                    >
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ============================================
// SIDEBAR COMPONENT
// ============================================
function Sidebar({ isHovered, onMouseEnter, onMouseLeave }) {
  const { t } = useUserPreferences();
  const collapsed = !isHovered;
  
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard') },
    { path: '/markets', icon: TrendingUp, label: t('markets') },
    { path: '/crypto', icon: Bitcoin, label: t('crypto') },
    { path: '/news', icon: Newspaper, label: t('news') },
    { path: '/analysis', icon: Brain, label: t('analysis') },
    { path: '/portfolio', icon: Wallet, label: t('portfolio') },
    { path: '/settings', icon: Settings, label: t('settings') },
  ];

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : 'expanded'}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="sidebar-header">
        <ElementalLogo collapsed={collapsed} />
      </div>

      <nav className="sidebar-nav">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            title={collapsed ? item.label : ''}
          >
            <item.icon size={20} className="nav-icon" />
            {!collapsed && <span className="nav-label">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="api-status connected">
          <span className="status-dot"></span>
          {!collapsed && <span>{t('connected')}</span>}
        </div>
      </div>
    </aside>
  );
}

// ============================================
// HEADER COMPONENT
// ============================================
function Header({ onSearch }) {
  const navigate = useNavigate();
  const { preferences, updatePreference, t, notifications, unreadCount, markNotificationRead, clearNotifications } = useUserPreferences();
  const { user, logout, isPro, isEnterprise } = useAuth();
  
  const [searchValue, setSearchValue] = useState('');
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showDatasetsDropdown, setShowDatasetsDropdown] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      onSearch(searchValue.trim().toUpperCase());
      setSearchValue('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setShowAccountMenu(false);
  };

  const languages = [
    { code: 'en', name: 'English', native: 'English' },
    { code: 'es', name: 'Spanish', native: 'Español' },
    { code: 'fr', name: 'French', native: 'Français' },
    { code: 'de', name: 'German', native: 'Deutsch' },
    { code: 'it', name: 'Italian', native: 'Italiano' },
    { code: 'pt', name: 'Portuguese', native: 'Português' },
    { code: 'zh', name: 'Chinese', native: '中文' },
    { code: 'ja', name: 'Japanese', native: '日本語' },
    { code: 'ko', name: 'Korean', native: '한국어' },
    { code: 'ar', name: 'Arabic', native: 'العربية' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
    { code: 'ru', name: 'Russian', native: 'Русский' },
    { code: 'nl', name: 'Dutch', native: 'Nederlands' },
    { code: 'pl', name: 'Polish', native: 'Polski' },
    { code: 'tr', name: 'Turkish', native: 'Türkçe' },
  ];

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.account-dropdown-wrapper')) {
        setShowAccountMenu(false);
        setActiveSubmenu(null);
      }
      if (!e.target.closest('.notifications-wrapper')) {
        setShowNotifications(false);
      }
      if (!e.target.closest('.datasets-dropdown-wrapper') && !e.target.closest('.mega-dropdown')) {
        setShowDatasetsDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const displayName = user?.name || preferences.name || 'User';
  const displayEmail = user?.email || preferences.email || '';

  return (
    <>
      <header className="main-header">
        {/* Datasets Dropdown */}
        <div className="datasets-dropdown-wrapper">
          <button 
            className={`header-nav-btn ${showDatasetsDropdown ? 'active' : ''}`}
            onClick={(e) => { 
              e.stopPropagation(); 
              setShowDatasetsDropdown(!showDatasetsDropdown);
              setShowAccountMenu(false);
              setShowNotifications(false);
            }}
          >
            <Database size={16} />
            <span>Datasets</span>
            <ChevronDown size={14} className={`nav-chevron ${showDatasetsDropdown ? 'rotated' : ''}`} />
          </button>
          
          <DatasetsMegaDropdown 
            isOpen={showDatasetsDropdown} 
            onClose={() => setShowDatasetsDropdown(false)}
            onNavigate={(path) => navigate(path)}
          />
        </div>

        <form className="header-search" onSubmit={handleSearch}>
          <Search size={18} className="search-icon" />
          <input type="text" placeholder={t('search')} value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
        </form>

        <div className="header-actions">
          {(isPro || isEnterprise) && (
            <div className={`subscription-badge ${isEnterprise ? 'enterprise' : 'pro'}`}>
              <Crown size={14} />
              <span>{isEnterprise ? 'Enterprise' : 'Pro'}</span>
            </div>
          )}

          <div className="notifications-wrapper">
            <button className={`header-btn notification-btn ${showNotifications ? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setShowNotifications(!showNotifications); setShowAccountMenu(false); setShowDatasetsDropdown(false); }}>
              <Bell size={18} />
              {unreadCount > 0 && <span className="notification-count">{unreadCount}</span>}
            </button>

            {showNotifications && (
              <div className="notifications-dropdown">
                <div className="notifications-header">
                  <h4>Notifications</h4>
                  {notifications.length > 0 && <button className="clear-all-btn" onClick={clearNotifications}>Clear All</button>}
                </div>
                <div className="notifications-list">
                  {notifications.length === 0 ? (
                    <div className="no-notifications"><Bell size={24} /><p>No notifications</p></div>
                  ) : notifications.map(notif => (
                    <div key={notif.id} className={`notification-item ${notif.read ? 'read' : 'unread'}`} onClick={() => markNotificationRead(notif.id)}>
                      <div className="notification-content"><p>{notif.message}</p><span className="notification-time">{notif.time}</span></div>
                      {!notif.read && <span className="unread-dot" />}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="account-dropdown-wrapper">
            <button className={`header-btn account-btn ${showAccountMenu ? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setShowAccountMenu(!showAccountMenu); setShowNotifications(false); setShowDatasetsDropdown(false); setActiveSubmenu(null); }}>
              <div className="account-avatar">{displayName.charAt(0).toUpperCase()}</div>
              <span className="account-name">{displayName}</span>
              <ChevronDown size={14} className={`account-chevron ${showAccountMenu ? 'rotated' : ''}`} />
            </button>

            {showAccountMenu && (
              <div className="account-dropdown">
                <div className="dropdown-profile">
                  <div className="profile-avatar">{displayName.charAt(0).toUpperCase()}</div>
                  <div className="profile-info">
                    <span className="profile-name">{displayName}</span>
                    <span className="profile-email">{displayEmail}</span>
                    {(isPro || isEnterprise) && (
                      <span className={`profile-tier ${isEnterprise ? 'enterprise' : 'pro'}`}>
                        <Crown size={12} />
                        {isEnterprise ? 'Enterprise' : 'Pro'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="dropdown-divider" />
                <button className="dropdown-item profile-link" onClick={() => { setShowProfile(true); setShowAccountMenu(false); }}>
                  <User size={16} /><span>View Profile</span><ChevronRight size={14} />
                </button>
                <div className="dropdown-divider" />

                {/* Language */}
                <div className="dropdown-expandable">
                  <button className={`dropdown-item has-submenu ${activeSubmenu === 'language' ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setActiveSubmenu(activeSubmenu === 'language' ? null : 'language'); }}>
                    <Languages size={16} /><span>Language</span>
                    <div className="item-value"><span>{preferences.language}</span>
                      <ChevronDown size={14} className={`expand-icon ${activeSubmenu === 'language' ? 'rotated' : ''}`} /></div>
                  </button>
                  {activeSubmenu === 'language' && (
                    <div className="inline-submenu">
                      {languages.map(lang => (
                        <button key={lang.code} className={`submenu-item ${preferences.language === lang.name ? 'selected' : ''}`}
                          onClick={() => { updatePreference('language', lang.name); setActiveSubmenu(null); }}>
                          <span className="submenu-name">{lang.name}</span><span className="submenu-native">{lang.native}</span>
                          {preferences.language === lang.name && <Check size={14} className="submenu-check" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="dropdown-divider" />
                <button className="dropdown-item" onClick={() => { navigate('/settings'); setShowAccountMenu(false); }}>
                  <Settings size={16} /><span>{t('settings')}</span>
                </button>
                <button className="dropdown-item logout" onClick={handleLogout}>
                  <LogOut size={16} /><span>Log Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {showProfile && (
        <UserProfile onClose={() => setShowProfile(false)} />
      )}
    </>
  );
}

// ============================================
// APP LAYOUT
// ============================================
function AppLayout({ children }) {
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const navigate = useNavigate();

  const handleOpenChart = useCallback((symbol) => {
    navigate(`/chart/${symbol}`);
  }, [navigate]);

  const handleSearch = useCallback((symbol) => {
    handleOpenChart(symbol);
  }, [handleOpenChart]);

  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { onOpenModal: handleOpenChart });
    }
    return child;
  });

  return (
    <div className={`app-layout ${sidebarHovered ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
      <Sidebar 
        isHovered={sidebarHovered}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      />
      <div className="main-content">
        <Header onSearch={handleSearch} />
        <main className="page-content">{childrenWithProps}</main>
      </div>
      <AIChatWidget />
    </div>
  );
}

// ============================================
// MAIN APP
// ============================================
function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <UserPreferencesProvider>
          <Router>
            <AppRoutes />
          </Router>
        </UserPreferencesProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

function AppRoutes() {
  const navigate = useNavigate();
  
  const handleAuthNavigate = (page) => navigate(`/${page}`);
  const handleAuthSuccess = () => navigate('/dashboard');

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/welcome" element={<LandingPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<AuthRoute><LoginPage onNavigate={handleAuthNavigate} onSuccess={handleAuthSuccess} /></AuthRoute>} />
      <Route path="/signup" element={<AuthRoute><SignupPage onNavigate={handleAuthNavigate} onSuccess={handleAuthSuccess} /></AuthRoute>} />
      <Route path="/forgot-password" element={<AuthRoute><ForgotPasswordPage onNavigate={handleAuthNavigate} /></AuthRoute>} />
      
      {/* Protected App Pages */}
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/markets" element={<ProtectedRoute><AppLayout><MarketsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/crypto" element={<ProtectedRoute><AppLayout><CryptoPage /></AppLayout></ProtectedRoute>} />
      <Route path="/analysis" element={<ProtectedRoute><AppLayout><AnalysisPage /></AppLayout></ProtectedRoute>} />
      <Route path="/news" element={<ProtectedRoute><AppLayout><NewsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/portfolio" element={<ProtectedRoute><AppLayout><PortfolioPage /></AppLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
      <Route path="/chart/:symbol" element={<ProtectedRoute><FullscreenChart /></ProtectedRoute>} />
      <Route path="/stock/:symbol" element={<ProtectedRoute><AppLayout><StockPage /></AppLayout></ProtectedRoute>} />

      {/* DataSets Routes */}
      <Route path="/datasets/congress" element={<ProtectedRoute><AppLayout><CongressPage /></AppLayout></ProtectedRoute>} />
      <Route path="/politician/:politicianId" element={<ProtectedRoute><AppLayout><PoliticianPage /></AppLayout></ProtectedRoute>} />
      <Route path="/datasets/insider" element={<ProtectedRoute><AppLayout><InsiderTradingPage /></AppLayout></ProtectedRoute>} />
      <Route path="/insider/:insiderId" element={<ProtectedRoute><AppLayout><InsiderProfilePage /></AppLayout></ProtectedRoute>} />
      <Route path="/datasets/institutional" element={<ProtectedRoute><AppLayout><InstitutionalHoldingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/institution/:institutionId" element={<ProtectedRoute><AppLayout><InstitutionProfilePage /></AppLayout></ProtectedRoute>} />
      <Route path="/datasets/lobbying" element={<ProtectedRoute><AppLayout><LobbyingPage /></AppLayout></ProtectedRoute>} />
      <Route path="/lobbying/:ticker" element={<ProtectedRoute><AppLayout><LobbyingDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/datasets/gov-contracts" element={<ProtectedRoute><AppLayout><GovernmentContractsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/contracts/:ticker" element={<ProtectedRoute><AppLayout><ContractDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/datasets/whales" element={<ProtectedRoute><AppLayout><WhaleMovesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/datasets/etf-holdings" element={<ProtectedRoute><AppLayout><ETFHoldingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/datasets/13f" element={<ProtectedRoute><AppLayout><ThirteenFPage /></AppLayout></ProtectedRoute>} />
      <Route path="/datasets/donations" element={<ProtectedRoute><AppLayout><PoliticalDonationsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/datasets/senate-disclosures" element={<ProtectedRoute><AppLayout><SenateDisclosuresPage /></AppLayout></ProtectedRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;